import React, { useEffect, useState, useRef } from 'react';
import { api, generateMockOrders } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const OrderNotifier = () => {
  const { isTestData, soundEnabled, soundVolume, keepAwake, soundInterval } = useSettings();
  const [unlocked, setUnlocked] = useState(false);
  
  const prevCountRef = useRef(0);
  const lastBeepTimeRef = useRef(0);
  const audioCtxRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Function to request Wake Lock (Keep Screen On)
  const requestWakeLock = async () => {
    if (keepAwake && 'wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('Screen Wake Lock is active');
        wakeLockRef.current.addEventListener('release', () => {
          console.log('Screen Wake Lock was released');
        });
      } catch (err) {
        console.error('Wake Lock failed:', err);
      }
    }
  };

  const unlock = async () => {
    if (unlocked) return;
    
    // Request Notification permissions
    if ('Notification' in window) {
      try {
        await Notification.requestPermission();
      } catch(e) {
        console.warn('Notification permission failed', e);
      }
    }

    // Initialize AudioContext to play loud beeps later (requires user interaction to unlock)
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    audioCtxRef.current = new AudioContext();
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }

    // Request screen wake lock
    await requestWakeLock();

    setUnlocked(true);
  };

  // Re-request wake lock when user comes back to the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && unlocked) {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [keepAwake, unlocked]);

  const playLoudBeep = () => {
    if (!soundEnabled || !audioCtxRef.current) return;
    
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume();

    const playTone = (freq, time, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.type = 'square';
      osc.frequency.setValueAtTime(freq, time);
      
      // Apply volume setting (0 to 1)
      const volumeLevel = soundVolume / 100;
      gain.gain.setValueAtTime(volumeLevel, time);
      
      osc.start(time);
      osc.stop(time + duration);
    };

    const now = ctx.currentTime;
    playTone(880, now, 0.15);
    playTone(880, now + 0.3, 0.15);
    playTone(880, now + 0.6, 0.15);
    playTone(1046.50, now + 0.9, 0.4);
  };

  const showNotification = async (count) => {
    if (Notification.permission === 'granted') {
      const title = '⚠️ Новые заказы!';
      const options = {
        body: `В очереди "New Orders" висит ${count} заказ(ов)!`,
        icon: '/icon-192.svg',
        badge: '/icon-192.svg',
        vibrate: [200, 100, 200, 100, 200, 100, 200],
        tag: 'new-order',
        requireInteraction: true
      };

      try {
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification(title, options);
        } else {
          new Notification(title, options);
        }
      } catch (e) {
        console.error('Service worker notification failed, falling back:', e);
        new Notification(title, options);
      }
    }
  };

  useEffect(() => {
    const checkOrders = async () => {
      try {
        let data = [];
        if (isTestData) {
          data = generateMockOrders(new Date(), new Date(), 25);
        } else {
          data = await api.fetchOrders(new Date());
        }

        const receivedCount = data.filter(o => o.status === 'Received').length;
        const now = Date.now();

        if (receivedCount > 0) {
          if (unlocked) {
            const isNewOrder = receivedCount > prevCountRef.current;
            const timeSinceLastBeep = now - lastBeepTimeRef.current;
            const isTimeForLoop = timeSinceLastBeep >= (soundInterval * 1000);

            // Play sound if there's a new order OR if it's time to loop
            if (isNewOrder || isTimeForLoop) {
              playLoudBeep();
              lastBeepTimeRef.current = now;
              
              // Only push notification if the order count actually increased
              if (isNewOrder) {
                showNotification(receivedCount);
              }
            }
          }
        }
        
        prevCountRef.current = receivedCount;
      } catch (err) {
        console.error('Notifier fetch error:', err);
      }
    };

    // Check frequently (every 5 seconds) to be responsive to the 10s/30s interval setting
    const interval = setInterval(checkOrders, 5000);
    return () => clearInterval(interval);
  }, [isTestData, unlocked, soundEnabled, soundVolume, soundInterval]);

  if (unlocked) return null;

  return (
    <div 
      onClick={unlock}
      className="fixed top-0 left-0 right-0 bg-yellow-400 text-black px-3 pb-3 z-[100] flex justify-center items-center gap-3 cursor-pointer shadow-xl border-b-4 border-yellow-500 hover:bg-yellow-300 transition-colors"
      style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
    >
      <SafeIcon icon={FiIcons.FiUnlock} className="animate-bounce" size={20} />
      <span className="font-black text-xs md:text-sm uppercase tracking-widest text-center">
        Нажмите для старта сессии (Уведомления и защита от сна)
      </span>
    </div>
  );
};

export default OrderNotifier;